import { useAuth } from '../../auth/auth-context';
import { can } from '../../lib/permissions';

/**
 * change-70 · acceso de escritura de las pantallas de administración, decidido SOLO por permisos
 * (AGENTS §13, Web 03). Una única tabla recurso → permiso que refleja lo que exige cada ruta del API:
 * la web oculta; el API autoriza. El ADMIN_INMOBILIARIA (solo `*.ver`) queda en solo lectura.
 */
export type RecursoAdmin = 'inmobiliaria' | 'proyecto' | 'catalogo' | 'usuario';

interface PermisosEscritura {
  crear: string;
  editar: string;
  desactivar: string | null;
  cargar: string | null;
}

const ESCRITURA: Record<RecursoAdmin, PermisosEscritura> = {
  // `PATCH` y `DELETE /real-estate-companies/:id` exigen `inmobiliaria.editar`.
  inmobiliaria: { crear: 'inmobiliaria.crear', editar: 'inmobiliaria.editar', desactivar: 'inmobiliaria.editar', cargar: null },
  // `PATCH`, `DELETE` y `POST /projects/:id/close` exigen `proyecto.editar`.
  proyecto: { crear: 'proyecto.crear', editar: 'proyecto.editar', desactivar: 'proyecto.editar', cargar: null },
  // Plantilla, validar y confirmar exigen `catalogo.cargar`; `PATCH /units/:id/price`, `catalogo.editar`.
  catalogo: { crear: 'catalogo.cargar', editar: 'catalogo.editar', desactivar: null, cargar: 'catalogo.cargar' },
  // `PATCH /users/:id/deactivate` exige `usuario.desactivar`; activar y reenviar, `usuario.editar`.
  usuario: { crear: 'usuario.crear', editar: 'usuario.editar', desactivar: 'usuario.desactivar', cargar: null },
};

export interface AccesoAdmin {
  crear: boolean;
  editar: boolean;
  desactivar: boolean;
  cargar: boolean;
  /** Ninguna escritura permitida: la pantalla es de consulta (textos y estados vacíos incluidos). */
  soloLectura: boolean;
}

export function accesoAdmin(permisos: readonly string[] | undefined, recurso: RecursoAdmin): AccesoAdmin {
  const tabla = ESCRITURA[recurso];
  const tiene = (permiso: string | null): boolean => permiso !== null && can(permisos, permiso);
  const crear = tiene(tabla.crear);
  const editar = tiene(tabla.editar);
  const desactivar = tiene(tabla.desactivar);
  const cargar = tiene(tabla.cargar);
  return { crear, editar, desactivar, cargar, soloLectura: !(crear || editar || desactivar || cargar) };
}

export function useAccesoAdmin(recurso: RecursoAdmin): AccesoAdmin {
  const { user } = useAuth();
  return accesoAdmin(user?.permissions, recurso);
}

/** Texto de gestión si hay alguna escritura; de consulta si la pantalla es de solo lectura. */
export function textoSegunAcceso(acceso: AccesoAdmin, textos: { gestion: string; consulta: string }): string {
  return acceso.soloLectura ? textos.consulta : textos.gestion;
}
